/**
 * Bandeau fixe de crédits avec achat Stripe + Mode Sombre/Clair
 * Design ultra professionnel et performant
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Coins, ShoppingCart, CreditCard, Sparkles, Check, Loader2, AlertCircle, Sun, Moon, Gift } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Card, CardContent } from './ui/card';
import axios from 'axios';
import { toast } from 'sonner';
import { useTheme } from '../contexts/ThemeContext';
import { getToken } from '../services/tokenService';
import OscopLogo from './brand/OscopLogo';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Forfaits de crédits disponibles
const CREDIT_PACKAGES = [
  { id: 'pack_50', credits: 50, price: 20, popular: false, bonus: 0 },
  { id: 'pack_180', credits: 180, price: 50, popular: false, bonus: 30 },
  { id: 'pack_260', credits: 260, price: 100, popular: true, bonus: 60 },
  { id: 'pack_2000', credits: 2000, price: 500, popular: false, bonus: 250 },
  { id: 'pack_5000', credits: 5000, price: 1000, popular: false, bonus: 750 },
];

// Composant Countdown pour les bonus programmés
const BonusCountdown = ({ bonus }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const targetDate = new Date(bonus.scheduled_date).getTime();
      const now = new Date().getTime();
      const difference = targetDate - now;

      if (difference <= 0) {
        setIsExpired(true);
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((difference % (1000 * 60)) / 1000)
      };
    };

    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [bonus.scheduled_date]);

  if (isExpired) return null;

  return (
    <div className="flex items-center gap-2 px-3 py-1.5 bg-[#DEA900]/15 border border-[#DEA900]/40 rounded-full backdrop-blur-sm" data-testid="bonus-countdown">
      <Gift className="h-3.5 w-3.5 text-[#f5cc3e] animate-pulse" />
      <span className="text-xs font-semibold text-[#f5cc3e]">
        +{bonus.credits}
      </span>
      <div className="flex items-center gap-1 text-xs font-mono">
        <span className="px-1.5 py-0.5 bg-[#0a3d62] text-[#f5cc3e] rounded text-[10px] tabular-nums">
          {String(timeLeft.days).padStart(2, '0')}j
        </span>
        <span className="px-1.5 py-0.5 bg-[#0a3d62] text-[#f5cc3e] rounded text-[10px] tabular-nums">
          {String(timeLeft.hours).padStart(2, '0')}h
        </span>
        <span className="px-1.5 py-0.5 bg-[#0a3d62] text-[#f5cc3e] rounded text-[10px] tabular-nums">
          {String(timeLeft.minutes).padStart(2, '0')}m
        </span>
        <span className="px-1.5 py-0.5 bg-[#0a3d62] text-[#f5cc3e] rounded text-[10px] tabular-nums animate-pulse">
          {String(timeLeft.seconds).padStart(2, '0')}s
        </span>
      </div>
    </div>
  );
};

export default function CreditBanner({ className = "" }) {
  const { isDarkMode, toggleTheme } = useTheme();
  const [walletBalance, setWalletBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [purchasing, setPurchasing] = useState(false);
  const [scheduledBonuses, setScheduledBonuses] = useState([]);

  // Charger le solde du wallet et les bonus programmés
  const loadWalletData = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const [walletRes, bonusRes] = await Promise.all([
        axios.get(`${API}/wallets/me`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/credits/scheduled-bonuses`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { bonuses: [] } }))
      ]);
      
      setWalletBalance(walletRes.data.credit_balance);
      setScheduledBonuses(bonusRes.data.bonuses || []);
    } catch {
      setWalletBalance(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadWalletData();
    const interval = setInterval(loadWalletData, 30000);
    return () => clearInterval(interval);
  }, [loadWalletData]);

  // Gérer l'achat de crédits via Stripe
  const handlePurchase = useCallback(async (pkg) => {
    setSelectedPackage(pkg);
    setPurchasing(true);

    const token = getToken();
    if (!token) {
      toast.error('Vous devez être connecté pour acheter des crédits');
      setPurchasing(false);
      return;
    }

    try {
      const response = await axios.post(
        `${API}/credits/purchase`,
        {
          package_id: pkg.id,
          credits: pkg.credits,
          amount: pkg.price,
          origin_url: window.location.origin
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Erreur lors de la création de la session de paiement');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'achat');
    } finally {
      setPurchasing(false);
    }
  }, []);

  // Calculer le prix par crédit
  const _getPricePerCredit = useCallback((pkg) => {
    return (pkg.price / pkg.credits).toFixed(3);
  }, []);

  // Calculer l'économie
  const getSavings = useCallback((pkg) => {
    const basePrice = 0.40;
    const actualPrice = pkg.price / pkg.credits;
    const savings = ((basePrice - actualPrice) / basePrice) * 100;
    return savings > 0 ? Math.round(savings) : 0;
  }, []);

  // Mémoriser le prochain bonus
  const nextBonus = useMemo(() => {
    if (!scheduledBonuses.length) return null;
    const upcoming = scheduledBonuses
      .filter(b => new Date(b.scheduled_date) > new Date() && b.status === 'pending')
      .sort((a, b) => new Date(a.scheduled_date) - new Date(b.scheduled_date));
    return upcoming[0] || null;
  }, [scheduledBonuses]);

  if (loading) return null;
  if (walletBalance === null) return null;

  const isLowBalance = walletBalance < 10;

  return (
    <>
      {/* ===== BANDEAU UC PREMIUM — Carte Crédit O'SCOP ===== */}
      <div className={`sticky top-0 z-50 group/header ${className}`} data-testid="credit-banner-ucc">
        <div className={`
          relative overflow-hidden
          bg-gradient-to-r from-[#0a3d62] via-[#0c4a78] to-[#08304d]
          shadow-[0_8px_32px_-12px_rgba(10,61,98,0.5)]
          border-b border-[#DEA900]/30
        `}>
          {/* Grain or filigrane (subtil) */}
          <div className="absolute inset-0 opacity-[0.07] mix-blend-overlay pointer-events-none" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40'%3E%3Ccircle cx='1' cy='1' r='1' fill='%23DEA900'/%3E%3C/svg%3E\")" }} />
          {/* Halo or top-right */}
          <div className="absolute -right-16 -top-16 w-48 h-48 rounded-full bg-[#DEA900]/15 blur-3xl pointer-events-none" />
          {/* Bande or fine en bas (signature carte premium) */}
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#DEA900] to-transparent" />
          {/* Shimmer animation */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#DEA900]/8 to-transparent -translate-x-full animate-shimmer pointer-events-none" />

          <div className="relative max-w-7xl mx-auto px-4 py-2.5">
            <div className="flex items-center justify-between gap-4 flex-wrap">

              {/* === Section gauche : Carte UC === */}
              <div className="flex items-center gap-3 sm:gap-4">
                {/* Logo OscopLogo officiel (avec halo or) */}
                <div className="relative shrink-0">
                  <div className="absolute inset-0 bg-[#DEA900]/20 rounded-xl blur-md" />
                  <div className="relative h-11 w-11 rounded-xl bg-gradient-to-br from-[#0a3d62] via-[#0c4a78] to-[#08304d] flex items-center justify-center ring-1 ring-[#DEA900]/40 shadow-lg shadow-[#0a3d62]/40">
                    <OscopLogo size={28} variant="on-navy" />
                  </div>
                  {isLowBalance && (
                    <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-rose-500 rounded-full ring-2 ring-[#0a3d62] animate-ping" />
                  )}
                </div>

                {/* Bloc Carte UC — façon carte de crédit premium */}
                <div className="flex items-center gap-3">
                  {/* Étiquette UC */}
                  <div className="hidden md:flex flex-col leading-tight pr-3 border-r border-[#DEA900]/25">
                    <span className="text-[9px] font-bold tracking-[0.2em] text-[#f5cc3e] uppercase">O'SCOP · UC</span>
                    <span className="text-[10px] uppercase tracking-wider text-white/55 leading-tight">Crédits coopératifs</span>
                  </div>

                  {/* Solde en chiffres tabulaires "carte" */}
                  <div className="flex flex-col leading-none">
                    <span className="text-[9px] uppercase tracking-[0.18em] font-semibold text-[#f5cc3e]/80 mb-0.5">
                      Solde disponible
                    </span>
                    <div className="flex items-baseline gap-2">
                      <Coins className="h-4 w-4 text-[#DEA900]" strokeWidth={2.4} />
                      <span className={`
                        text-2xl sm:text-3xl font-black tracking-tight tabular-nums
                        text-white
                        ${isLowBalance ? 'animate-pulse' : ''}
                      `}>
                        {walletBalance.toLocaleString('fr-FR')}
                      </span>
                      <span className="text-xs font-medium text-white/65 uppercase tracking-wider">
                        UC
                      </span>
                    </div>
                  </div>
                </div>

                {/* Badge Solde Faible (au lieu du gradient agressif) */}
                {isLowBalance && (
                  <Badge className="bg-rose-500/15 text-[#fda4af] border border-rose-400/40 backdrop-blur-sm animate-pulse font-semibold" data-testid="low-balance-badge">
                    <AlertCircle className="h-3 w-3 mr-1" />
                    Solde faible
                  </Badge>
                )}

                {/* Bonus programmé */}
                {nextBonus && <BonusCountdown bonus={nextBonus} />}
              </div>

              {/* === Section droite : Actions === */}
              <div className="flex items-center gap-2 sm:gap-3">
                {/* Toggle Dark/Light Mode — adapté au navy/or */}
                <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/8 border border-[#DEA900]/20 backdrop-blur-sm">
                  <Sun className={`h-4 w-4 transition-all ${isDarkMode ? 'text-white/30' : 'text-[#f5cc3e]'}`} />
                  <Switch
                    checked={isDarkMode}
                    onCheckedChange={toggleTheme}
                    className="data-[state=checked]:bg-[#DEA900] data-[state=unchecked]:bg-white/15"
                  />
                  <Moon className={`h-4 w-4 transition-all ${isDarkMode ? 'text-[#f5cc3e]' : 'text-white/30'}`} />
                </div>

                {/* Bouton Acheter — or institutionnel */}
                <Button
                  onClick={() => setShowPurchaseModal(true)}
                  className="group/btn relative overflow-hidden bg-gradient-to-r from-[#DEA900] to-[#b88600] hover:from-[#f5cc3e] hover:to-[#DEA900] text-[#0a3d62] font-bold border-0 shadow-lg shadow-[#DEA900]/30 transition-all duration-300 hover:shadow-xl hover:shadow-[#DEA900]/40 hover:-translate-y-0.5"
                  data-testid="ucc-purchase-btn"
                >
                  <ShoppingCart className="h-4 w-4 mr-2 transition-transform group-hover/btn:scale-110" />
                  <span className="hidden sm:inline">Acheter des </span>UC
                  <Sparkles className="h-3 w-3 ml-1.5 text-[#0a3d62] transition-transform group-hover/btn:rotate-180" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ===== Modal d'achat de crédits — Premium UC ===== */}
      <Dialog open={showPurchaseModal} onOpenChange={setShowPurchaseModal}>
        <DialogContent className={`
          sm:max-w-4xl max-h-[90vh] overflow-y-auto p-0
          ${isDarkMode ? 'bg-slate-900 border-slate-700 text-white' : 'bg-white'}
        `} data-testid="ucc-modal">
          {/* Header navy O'SCOP */}
          <div className="relative overflow-hidden bg-gradient-to-r from-[#0a3d62] to-[#0c4a78] -mx-6 -mt-6 mb-0 p-6 rounded-t-lg">
            <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40'%3E%3Ccircle cx='1' cy='1' r='1' fill='%23DEA900'/%3E%3C/svg%3E\")" }} />
            <div className="absolute -right-12 -top-12 w-40 h-40 rounded-full bg-[#DEA900]/15 blur-2xl" />
            <DialogHeader className="relative">
              <DialogTitle className="flex items-center gap-3 text-white text-2xl">
                <div className="h-11 w-11 rounded-xl bg-[#DEA900] flex items-center justify-center shadow-md shadow-[#DEA900]/30 ring-1 ring-[#f5cc3e]/40">
                  <OscopLogo size={28} variant="on-navy" />
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.18em] font-bold text-[#f5cc3e] uppercase mb-0.5">O'SCOP · Outremer</div>
                  <div className="leading-none">Acheter des UC</div>
                </div>
              </DialogTitle>
              <DialogDescription className="text-white/75 mt-2">
                Choisissez le forfait qui correspond à vos besoins. Plus vous achetez, plus vous économisez.
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="px-6 pb-6">
            {/* Solde actuel — encart premium */}
            <div className={`relative p-4 rounded-xl mb-5 mt-2 border ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-gradient-to-br from-[#0a3d62]/3 to-[#DEA900]/5 border-[#0a3d62]/15'}`}>
              <span className="absolute left-0 top-4 bottom-4 w-1 rounded-r-full bg-[#DEA900]" />
              <div className="flex items-center justify-between pl-3">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Votre solde actuel</p>
                  <div className="flex items-baseline gap-2 mt-0.5">
                    <Coins className="h-5 w-5 text-[#DEA900]" strokeWidth={2.4} />
                    <span className={`text-3xl font-bold tabular-nums tracking-tight ${isDarkMode ? 'text-white' : 'text-[#0a3d62]'}`}>{walletBalance.toLocaleString('fr-FR')}</span>
                    <span className="text-sm text-slate-500 font-medium uppercase tracking-wider">UC</span>
                  </div>
                </div>
                {isLowBalance && (
                  <Badge className="bg-rose-700 text-white border border-rose-900 font-semibold">
                    <AlertCircle className="h-3 w-3 mr-1" /> À recharger
                  </Badge>
                )}
              </div>
            </div>

            {/* Grille des forfaits — 3 colonnes desktop / 2 tablette / 1 mobile */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {CREDIT_PACKAGES.map((pkg) => {
                const savings = getSavings(pkg);
                const isSelected = selectedPackage?.id === pkg.id;
                return (
                  <Card
                    key={pkg.id}
                    className={`
                      relative cursor-pointer transition-all duration-300 hover:-translate-y-0.5 flex flex-col
                      ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white'}
                      ${pkg.popular
                        ? 'border-2 border-[#DEA900] shadow-[0_10px_30px_-12px_rgba(222,169,0,0.35)] ring-1 ring-[#DEA900]/20'
                        : 'border border-slate-200 hover:border-[#0a3d62]/30 hover:shadow-[0_10px_30px_-12px_rgba(10,61,98,0.18)]'
                      }
                      ${isSelected ? 'ring-2 ring-[#DEA900] ring-offset-2 dark:ring-offset-slate-900' : ''}
                    `}
                    onClick={() => setSelectedPackage(pkg)}
                    data-testid={`ucc-package-${pkg.id}`}
                  >
                    {pkg.popular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                        <Badge className="bg-gradient-to-r from-[#DEA900] to-[#b88600] text-[#0a3d62] font-bold border-0 shadow-md shadow-[#DEA900]/40 px-2.5 py-0.5 text-[10px]">
                          <Sparkles className="h-3 w-3 mr-1" />
                          POPULAIRE
                        </Badge>
                      </div>
                    )}

                    {savings > 0 && (
                      <div className="absolute -top-2 -right-2 z-10">
                        <Badge className="bg-emerald-600 text-white font-semibold text-[10px] shadow-md shadow-emerald-600/30 border-0 px-2 py-0.5">
                          -{savings}%
                        </Badge>
                      </div>
                    )}

                    <CardContent className="p-5 flex flex-col flex-1">
                      {/* Bloc Quantité */}
                      <div className="text-center">
                        <div className="flex items-baseline justify-center gap-1.5">
                          <Coins className="h-5 w-5 text-[#DEA900] self-center" strokeWidth={2.4} />
                          <span className={`text-[34px] leading-none font-black tabular-nums tracking-tight ${isDarkMode ? 'text-white' : 'text-[#0a3d62]'}`}>
                            {pkg.credits.toLocaleString('fr-FR')}
                          </span>
                        </div>
                        <span className={`block mt-1 text-[11px] uppercase tracking-[0.18em] font-semibold ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>UC</span>
                      </div>

                      {/* Bonus (réserve toujours la place) */}
                      <div className="mt-2.5 min-h-[28px] flex items-center justify-center">
                        {pkg.bonus > 0 && (
                          <div className="text-emerald-700 text-[11px] font-semibold inline-flex items-center gap-1 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                            <Gift className="h-3 w-3" />
                            dont {pkg.bonus} bonus offerts
                          </div>
                        )}
                      </div>

                      {/* Prix */}
                      <div className={`text-center py-3 mt-2 border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                        <span className={`text-2xl font-bold tabular-nums ${isDarkMode ? 'text-white' : 'text-[#0a3d62]'}`}>
                          {pkg.price}€
                        </span>
                      </div>

                      {/* Bouton — collé en bas */}
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePurchase(pkg);
                        }}
                        disabled={purchasing}
                        className={`w-full mt-auto font-semibold transition-all h-10 ${
                          pkg.popular
                            ? 'bg-gradient-to-r from-[#DEA900] to-[#b88600] hover:from-[#f5cc3e] hover:to-[#DEA900] text-[#0a3d62] shadow-md shadow-[#DEA900]/30'
                            : 'bg-[#0a3d62] hover:bg-[#0c4a78] text-white'
                        }`}
                        data-testid={`ucc-buy-${pkg.id}`}
                      >
                        {purchasing && selectedPackage?.id === pkg.id ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Redirection…
                          </>
                        ) : (
                          <>
                            <CreditCard className="h-4 w-4 mr-2" />
                            Acheter maintenant
                          </>
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Footer sécurité */}
            <div className={`mt-6 p-4 rounded-xl ${isDarkMode ? 'bg-slate-800' : 'bg-gradient-to-br from-[#0a3d62]/3 to-[#DEA900]/3 border border-slate-200'}`}>
              <div className={`flex items-center justify-center gap-4 flex-wrap text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-700'}`}>
                <div className="flex items-center gap-1.5">
                  <Check className="h-4 w-4 text-emerald-600" />
                  <span className="font-medium">Paiement sécurisé Stripe</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Check className="h-4 w-4 text-emerald-600" />
                  <span className="font-medium">Crédits immédiats</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Check className="h-4 w-4 text-emerald-600" />
                  <span className="font-medium">Sans engagement</span>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Styles pour l'animation shimmer */}
      <style>{`
        @keyframes shimmer {
          100% { transform: translateX(100%); }
        }
        .animate-shimmer {
          animation: shimmer 3s infinite;
        }
      `}</style>
    </>
  );
}
